// src/services/pdf/core/PDFDocument.ts
// Thin wrapper around jsPDF providing layout helpers and cursor management.

import { jsPDF } from "jspdf";
import { PAGE_LAYOUT } from "./layout";
import { PDF_COLORS, type RGBColor } from "./colors";

export class PDFDocument {
  private doc: jsPDF;
  private cursorY: number;

  constructor() {
    this.doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    this.cursorY = PAGE_LAYOUT.marginTop;
  }

  // ─── Cursor Management ──────────────────────────────────────────────────

  get y(): number {
    return this.cursorY;
  }

  set y(value: number) {
    this.cursorY = value;
  }

  advanceY(amount: number): void {
    this.cursorY += amount;
  }

  get contentLeft(): number {
    return PAGE_LAYOUT.marginLeft;
  }

  get contentRight(): number {
    return PAGE_LAYOUT.pageWidth - PAGE_LAYOUT.marginRight;
  }

  get contentWidth(): number {
    return PAGE_LAYOUT.contentWidth;
  }

  get remainingHeight(): number {
    return PAGE_LAYOUT.pageHeight - PAGE_LAYOUT.marginBottom - this.cursorY;
  }

  // ─── Page Management ────────────────────────────────────────────────────

  addPage(): void {
    this.doc.addPage();
    this.cursorY = PAGE_LAYOUT.marginTop;
  }

  ensureSpace(needed: number): void {
    if (this.remainingHeight < needed) {
      this.addPage();
    }
  }

  // ─── Text Rendering ─────────────────────────────────────────────────────

  setFont(style: "normal" | "bold" | "italic" = "normal", size?: number): void {
    this.doc.setFont("helvetica", style);
    if (size) this.doc.setFontSize(size);
  }

  setColor(color: RGBColor): void {
    this.doc.setTextColor(color.r, color.g, color.b);
  }

  setDrawColor(color: RGBColor): void {
    this.doc.setDrawColor(color.r, color.g, color.b);
  }

  setFillColor(color: RGBColor): void {
    this.doc.setFillColor(color.r, color.g, color.b);
  }

  text(
    content: string,
    x: number,
    options?: {
      maxWidth?: number;
      align?: "left" | "center" | "right";
      color?: RGBColor;
      size?: number;
      style?: "normal" | "bold" | "italic";
    }
  ): void {
    if (options?.color) this.setColor(options.color);
    if (options?.style) this.setFont(options.style);
    if (options?.size) this.doc.setFontSize(options.size);

    this.doc.text(content, x, this.cursorY, {
      maxWidth: options?.maxWidth ?? this.contentWidth,
      align: options?.align ?? "left",
    });
  }

  /** Render text and advance cursor by one line */
  writeLine(
    content: string,
    options?: {
      x?: number;
      maxWidth?: number;
      color?: RGBColor;
      size?: number;
      style?: "normal" | "bold" | "italic";
      lineHeight?: number;
    }
  ): void {
    const x = options?.x ?? this.contentLeft;
    this.text(content, x, {
      maxWidth: options?.maxWidth,
      color: options?.color,
      size: options?.size,
      style: options?.style,
    });
    this.advanceY(options?.lineHeight ?? PAGE_LAYOUT.lineHeight);
  }

  /** Write multi-line text, splitting by the available width */
  writeWrapped(
    content: string,
    options?: {
      x?: number;
      maxWidth?: number;
      color?: RGBColor;
      size?: number;
      style?: "normal" | "bold" | "italic";
      lineHeight?: number;
    }
  ): void {
    const x = options?.x ?? this.contentLeft;
    const maxW = options?.maxWidth ?? this.contentWidth;
    const lh = options?.lineHeight ?? PAGE_LAYOUT.smallLineHeight;

    if (options?.color) this.setColor(options.color);
    if (options?.style) this.setFont(options.style);
    if (options?.size) this.doc.setFontSize(options.size);

    const lines = this.doc.splitTextToSize(content, maxW) as string[];
    for (const line of lines) {
      this.ensureSpace(lh + 2);
      this.doc.text(line, x, this.cursorY);
      this.advanceY(lh);
    }
  }

  // ─── Drawing Helpers ────────────────────────────────────────────────────

  drawLine(
    x1: number,
    y: number,
    x2: number,
    color: RGBColor = PDF_COLORS.border
  ): void {
    this.setDrawColor(color);
    this.doc.setLineWidth(0.2);
    this.doc.line(x1, y, x2, y);
  }

  drawHorizontalRule(color?: RGBColor): void {
    this.drawLine(this.contentLeft, this.cursorY, this.contentRight, color);
    this.advanceY(PAGE_LAYOUT.subsectionGap);
  }

  drawRect(
    x: number,
    y: number,
    w: number,
    h: number,
    options?: { fill?: RGBColor; stroke?: RGBColor; radius?: number }
  ): void {
    if (options?.fill) this.setFillColor(options.fill);
    if (options?.stroke) this.setDrawColor(options.stroke);

    if (options?.radius) {
      this.doc.roundedRect(
        x, y, w, h,
        options.radius, options.radius,
        options?.fill && options?.stroke ? "FD" : options?.fill ? "F" : "S"
      );
    } else {
      this.doc.rect(
        x, y, w, h,
        options?.fill && options?.stroke ? "FD" : options?.fill ? "F" : "S"
      );
    }
  }

  drawCircle(
    x: number,
    y: number,
    radius: number,
    fill: RGBColor
  ): void {
    this.setFillColor(fill);
    this.doc.circle(x, y, radius, "F");
  }

  /** Draw a small colored badge with text */
  drawBadge(
    x: number,
    y: number,
    text: string,
    bgColor: RGBColor,
    textColor: RGBColor,
    options?: { width?: number; height?: number }
  ): void {
    const w = options?.width ?? this.getTextWidth(text) + 6;
    const h = options?.height ?? 5;
    this.drawRect(x, y - h + 1.5, w, h, { fill: bgColor, radius: 1.5 });
    this.setColor(textColor);
    this.doc.text(text, x + 3, y);
  }

  // ─── Measurement ────────────────────────────────────────────────────────

  getTextWidth(text: string): number {
    return this.doc.getTextWidth(text);
  }

  getTextHeight(text: string, maxWidth: number): number {
    const lines = this.doc.splitTextToSize(text, maxWidth) as string[];
    return lines.length * PAGE_LAYOUT.smallLineHeight;
  }

  // ─── Output ─────────────────────────────────────────────────────────────

  save(filename: string): void {
    this.doc.save(filename);
  }

  getBlob(): Blob {
    return this.doc.output("blob");
  }

  getDataUri(): string {
    return this.doc.output("datauristring");
  }
}
